import { useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import LinkIcon from '@atlaskit/icon/core/link';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { wacToken } from '@/libraries/design/foundations/index.ts';

export interface AccordionCopyLinkProps {
	id: string;
	header: string;
}

export const AccordionCopyLink = (props: AccordionCopyLinkProps) => {
	const { id, header } = props;
	const intl = useIntl();
	const [copied, setCopied] = useState(false);
	const tooltipContent = copied
		? intl.formatMessage({
				id: 'Accordion.CopyLink.copied',
				defaultMessage: 'Copied!',
			})
		: intl.formatMessage({
				id: 'Accordion.CopyLink.tooltip',
				defaultMessage: 'Copy link to heading',
			});

	const handleCopy = async () => {
		setCopied(false);
		const url = new URL(window.location.href);
		url.hash = id;

		try {
			await navigator.clipboard.writeText(url.toString());
			setCopied(true);
		} catch {}
	};

	return (
		<span css={copyLinkContainerCss}>
			<Tooltip content={tooltipContent} delay={0}>
				{(tooltipProps) => (
					<button
						{...tooltipProps}
						type="button"
						css={copyLinkCss}
						aria-label={intl.formatMessage(
							{
								id: 'Accordion.CopyLink.label',
								defaultMessage: 'Copy link to {heading}',
							},
							{ heading: header },
						)}
						onClick={handleCopy}
					>
						<LinkIcon label="" />
					</button>
				)}
			</Tooltip>
			<VisuallyHidden>
				<span role="status">{copied && tooltipContent}</span>
			</VisuallyHidden>
		</span>
	);
};

const copyLinkContainerCss = css({
	position: 'relative',
	display: 'inline-flex',
	flexShrink: 0,
	zIndex: 1,
});

const copyLinkCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: token('space.400'),
	height: token('space.400'),
	padding: 0,
	border: 0,
	backgroundColor: 'transparent',
	color: wacToken('color.extended.blue800'),
	cursor: 'pointer',
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.extended.blue800')}`,
		outlineOffset: token('space.050'),
	},
});
